import React, { useState, useEffect, useCallback } from 'react';
import { ConsoleShell, useConsoleTheme } from '../components/Console';
import { MetricCard, Card, CardTitle, Grid, Insight, Btn } from '../components/UI';
import { supabase } from '../supabaseClient';
import EditLocationModal from '../components/EditLocationModal';
import { getDisplayAddress } from '../utils/location';

const F = 'system-ui,-apple-system,"Helvetica Neue",Arial,sans-serif';
const CACHE_KEY = 'sanoire_dq_stats_v2';
const CACHE_TTL = 20 * 60 * 1000; // 20 min

function fmt(n)     { return n == null ? '…' : Number(n).toLocaleString(); }
function pct(a, b)  { return b ? (a / b * 100).toFixed(1) + '%' : '0%'; }
function pctN(a, b) { return b ? parseFloat((a / b * 100).toFixed(1)) : 0; }

function useIsMobile() {
  const [mob, setMob] = React.useState(() => typeof window !== 'undefined' && window.innerWidth < 768);
  React.useEffect(() => {
    const h = () => setMob(window.innerWidth < 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return mob;
}

// ── Reusable fill bar ──────────────────────────────────────────────────────
function FieldBar({ label, fill, total, color = '#2da06a', critical, warn }) {
  const { t } = useConsoleTheme(); // eslint-disable-line no-unused-vars
  const p = pctN(fill, total);
  const missing = total - fill;
  const clr = p >= 90 ? '#2da06a' : p >= 50 ? '#d97706' : '#e05c5c';
  return (
    <div style={{ marginBottom: 11 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3, alignItems: 'center' }}>
        <span style={{ fontSize: '.72rem', color: '#314F5C', fontWeight: 500 }}>
          {label}
          {critical && <span style={{ marginLeft: 5, fontSize: '.57rem', fontWeight: 700, color: '#fff', background: '#e05c5c', padding: '1px 5px', borderRadius: 3 }}>CRITICAL</span>}
          {warn    && <span style={{ marginLeft: 5, fontSize: '.57rem', fontWeight: 700, color: '#7d4e00', background: '#fef3c7', padding: '1px 5px', borderRadius: 3 }}>LOW</span>}
        </span>
        <span style={{ fontSize: '.67rem', color: clr, fontWeight: 600 }}>
          {p}% <span style={{ color: '#9eb8c8', fontWeight: 400 }}>· {fmt(missing)} missing</span>
        </span>
      </div>
      <div style={{ height: 6, background: '#edf6fb', borderRadius: 999, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: p + '%', background: color, borderRadius: 999 }} />
      </div>
    </div>
  );
}

// ── Pipeline status bar ────────────────────────────────────────────────────
function PipelineBar({ label, done, partial, failed, nullCount, total }) {
  const { t } = useConsoleTheme(); // eslint-disable-line no-unused-vars
  const dP = pctN(done, total), pP = pctN(partial, total),
        fP = pctN(failed, total), nP = pctN(nullCount, total);
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
        <span style={{ fontSize: '.72rem', color: '#314F5C', fontWeight: 500 }}>{label}</span>
        <span style={{ fontSize: '.65rem', color: '#9eb8c8' }}>
          <span style={{ color: '#2da06a' }}>{fmt(done)} done</span>
          {partial > 0 && <span style={{ color: '#6F8FA7' }}> · {fmt(partial)} partial</span>}
          <span style={{ color: '#e05c5c' }}> · {fmt(failed)} failed</span>
          <span style={{ color: '#9eb8c8' }}> · {fmt(nullCount)} pending</span>
        </span>
      </div>
      <div style={{ height: 8, background: '#edf6fb', borderRadius: 999, overflow: 'hidden', display: 'flex' }}>
        <div style={{ width: dP + '%', background: '#2da06a' }} />
        {partial > 0 && <div style={{ width: pP + '%', background: '#6F8FA7' }} />}
        <div style={{ width: fP + '%', background: '#e05c5c' }} />
        <div style={{ width: nP + '%', background: '#dde8ef' }} />
      </div>
    </div>
  );
}

// ── Sort header ────────────────────────────────────────────────────────────
function SortHd({ col, label, sortCol, sortAsc, onSort }) {
  const { t } = useConsoleTheme(); // eslint-disable-line no-unused-vars
  const active = sortCol === col;
  return (
    <span onClick={() => onSort(col)} style={{ cursor: 'pointer', fontSize: '.57rem', color: active ? '#314F5C' : '#9eb8c8', textTransform: 'uppercase', letterSpacing: '.06em', userSelect: 'none', fontWeight: active ? 600 : 400 }}>
      {label}{active ? (sortAsc ? ' ↑' : ' ↓') : ''}
    </span>
  );
}

function DataQualityBody() {
  const { t } = useConsoleTheme(); // eslint-disable-line no-unused-vars
  const [stats,      setStats]     = useState(null);
  const [continents, setContinents]= useState([]);
  const [countries,  setCountries] = useState([]);
  const [flagged,    setFlagged]   = useState([]);
  const [loading,    setLoading]   = useState(true);
  const [refreshing, setRefreshing]= useState(false);
  const [lastFetched,setLastFetch] = useState(null);
  const [flagPage,   setFlagPage]  = useState(0);
  const [sortCol,    setSortCol]   = useState('total');
  const [sortAsc,    setSortAsc]   = useState(false);
  const [cSortCol,   setCSortCol]  = useState('total');
  const [cSortAsc,   setCSortAsc]  = useState(false);
  const [editing,    setEditing]   = useState(null);
  const [activeTab,  setActiveTab] = useState('country'); // 'country' | 'continent'
  const FLAG_PAGE_SIZE = 15;
  const isMobile = useIsMobile();

  const FLAG_LABELS = { name_too_short:'Name too short', pipe_in_name:'Garbage name', missing_name:'Missing name', missing_address:'Missing address', other:'Other' };
  const FLAG_COLORS = { name_too_short:'#d97706', pipe_in_name:'#e05c5c', missing_name:'#e05c5c', missing_address:'#d97706', other:'#6F8FA7' };

  // ── Load: cache-first, background refresh ─────────────────────────────
  // readFromCache: reads the pre-computed cache tables (instant)
  const readFromCache = useCallback(async () => {
    try {
      const [sRes, contRes, ctryRes, flagRes] = await Promise.all([
        supabase.rpc('get_data_quality_stats'),
        supabase.rpc('get_quality_by_continent'),
        supabase.rpc('get_premium_by_country'),
        supabase.rpc('get_flagged_locations', { p_limit: 200 }),
      ]);
      const s = sRes.data   || null;
      const c = contRes.data|| [];
      const k = ctryRes.data|| [];
      const f = flagRes.data|| [];
      if (s) {
        setStats(s);
        // Use computed_at from stats cache if available
        if (s.computed_at) setLastFetch(new Date(s.computed_at));
        else setLastFetch(new Date());
      }
      setContinents(c);
      setCountries(k);
      setFlagged(f);
      // Also persist to localStorage for instant next load
      try { localStorage.setItem(CACHE_KEY, JSON.stringify({ ts: Date.now(), stats: s, continents: c, countries: k, flagged: f })); } catch (_e) {}
    } catch (_e) {}
    setLoading(false);
  }, []);

  const fetchAll = useCallback(async (force = false) => {
    // Try localStorage first for instant render
    if (!force) {
      try {
        const cached = JSON.parse(localStorage.getItem(CACHE_KEY) || 'null');
        if (cached && Date.now() - cached.ts < CACHE_TTL) {
          setStats(cached.stats);
          setContinents(cached.continents || []);
          setCountries(cached.countries || []);
          setFlagged(cached.flagged || []);
          if (cached.stats?.computed_at) setLastFetch(new Date(cached.stats.computed_at));
          else setLastFetch(new Date(cached.ts));
          setLoading(false);
          // Still read fresh from Supabase cache in background (fast)
          setTimeout(() => readFromCache(), 500);
          return;
        }
      } catch (_e) {}
      // No local cache — read from Supabase cache tables
      await readFromCache();
      return;
    }

    // force=true: trigger a full recompute of the Supabase cache, then read
    setRefreshing(true);
    try {
      await supabase.rpc('refresh_dq_cache');
    } catch (_e) {}
    await readFromCache();
    setRefreshing(false);
  }, [readFromCache]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const s = stats || {};
  const total = s.total || 0;

  function toggleSort(col) { if (sortCol===col) setSortAsc(a=>!a); else { setSortCol(col); setSortAsc(false); } }
  function toggleCSort(col) { if (cSortCol===col) setCSortAsc(a=>!a); else { setCSortCol(col); setCSortAsc(false); } }

  const sortedCountries  = [...countries].sort((a,b)  => { const av=a[sortCol]??0,bv=b[sortCol]??0; return sortAsc?av-bv:bv-av; });
  const sortedContinents = [...continents].sort((a,b) => { const av=a[cSortCol]??0,bv=b[cSortCol]??0; return cSortAsc?av-bv:bv-av; });
  const flaggedPage = flagged.slice(flagPage*FLAG_PAGE_SIZE, (flagPage+1)*FLAG_PAGE_SIZE);
  const flagGroups  = flagged.reduce((a,r) => { a[r.flag_reason]=(a[r.flag_reason]||0)+1; return a; }, {});

  function handleFlaggedSaved(updated) {
    if (updated === null) setFlagged(f => f.filter(r => String(r.id) !== String(editing?.id)));
    else setFlagged(f => f.map(r => String(r.id)===String(updated.id) ? {...r,...updated} : r));
    if (updated === null) setEditing(null);
  }

  const TabBtn = ({ id, label }) => (
    <button onClick={() => setActiveTab(id)} style={{ padding: '4px 12px', fontSize: '.65rem', fontWeight: activeTab===id ? 600 : 400, background: activeTab===id ? '#314F5C' : 'transparent', color: activeTab===id ? '#fff' : '#6F8FA7', border: '1px solid ' + (activeTab===id ? '#314F5C' : '#d8eaf4'), borderRadius: 6, cursor: 'pointer', fontFamily: F }}>
      {label}
    </button>
  );

  const shRow = { display: 'grid', gap: 6, padding: '7px 0', borderBottom: '.5px solid rgba(49,79,92,.06)', alignItems: 'center' };

  return (
    <div style={{ fontFamily: F }}>
      {editing && <EditLocationModal record={editing} onClose={() => setEditing(null)} onSaved={handleFlaggedSaved} />}

      {/* ── Header row ── */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginBottom: 8, gap: 10 }}>
        {lastFetched && <span style={{ fontSize: '.62rem', color: '#9eb8c8' }}>Updated {lastFetched.toLocaleTimeString()}</span>}
        <button onClick={() => fetchAll(true)} disabled={refreshing}
          style={{ padding: '4px 12px', fontSize: '.65rem', background: refreshing ? '#edf6fb' : '#314F5C', color: refreshing ? '#9eb8c8' : '#fff', border: 'none', borderRadius: 6, cursor: refreshing ? 'default' : 'pointer', fontFamily: F }}>
          {refreshing ? 'Refreshing…' : '↻ Refresh'}
        </button>
      </div>

      {/* ── KPIs ── */}
      <Grid cols={4}>
        <MetricCard label="Total locations"   value={loading ? '…' : fmt(total)}                                sub="in database" />
        <MetricCard label="Premium"           value={loading ? '…' : fmt(s.premium||0)}                        sub={loading?'':pct(s.premium,total)+' of total'} trend={(s.premium||0)>0?'up':null} accent="#9F7E4F" />
        <MetricCard label="Missing GPS"       value={loading ? '…' : fmt(s.missing_coords||0)}                 sub={loading?'':pct(s.missing_coords,total)+' — critical'} trend="down" accent="#e05c5c" />
        <MetricCard label="Flagged records"   value={loading ? '…' : fmt(s.flagged_count||0)}                  sub={loading?'':pct(s.flagged_count,total)+' need review'} trend={(s.flagged_count||0)>0?'down':null} accent="#d97706" />
      </Grid>

      {/* ── Alerts ── */}
      {!loading && (s.geocode_null||0) === total && total > 0 && (
        <Insight type="warn">GPS pipeline has never run — {fmt(total)} locations have no coordinates. Proximity search, map views and Sana "near me" are all non-functional until geocoding completes.</Insight>
      )}
      {!loading && (s.enrich_failed||0) > total * 0.1 && (
        <Insight type="warn">{fmt(s.enrich_failed||0)} locations ({pct(s.enrich_failed,total)}) failed enrichment — booking URL and specialties URL fill rates will remain near zero until these are re-run.</Insight>
      )}

      {/* ── Field completeness ── */}
      <div style={{ display: isMobile?'block':'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:16 }}>
        <Card style={{ marginBottom: isMobile?16:0 }}>
          <CardTitle>Field completeness — contact & identity</CardTitle>
          {loading ? <div style={{ color:t.mut, fontSize:'.75rem' }}>Loading…</div> : <>
            <FieldBar label="Name"         fill={total-(s.flagged_count||0)} total={total} color="#2da06a" />
            <FieldBar label="Address"      fill={s.has_address||0}      total={total} color="#2da06a" />
            <FieldBar label="Phone"        fill={s.has_phone||0}        total={total} color="#2da06a" />
            <FieldBar label="Website"      fill={s.has_website||0}      total={total} color="#2da06a" />
            <FieldBar label="Languages"    fill={s.has_languages||0}    total={total} color="#6F8FA7" />
            <FieldBar label="Country code" fill={s.has_country_code||0} total={total} color="#6F8FA7" />
            <FieldBar label="Continent"    fill={s.has_continent||0}    total={total} color="#6F8FA7" />
            <FieldBar label="ER / 24h"     fill={s.has_er_24_7||0}      total={total} color="#6F8FA7" />
            <FieldBar label="Telehealth"   fill={s.has_telehealth||0}   total={total} color="#6F8FA7" />
          </>}
        </Card>

        <Card style={{ marginBottom:0 }}>
          <CardTitle>Field completeness — premium & geo</CardTitle>
          {loading ? <div style={{ color:t.mut, fontSize:'.75rem' }}>Loading…</div> : <>
            <FieldBar label="GPS coordinates" fill={s.has_latitude||0}       total={total} color="#e05c5c" critical />
            <FieldBar label="Booking URL"     fill={s.has_booking_url||0}    total={total} color="#d97706" warn={pctN(s.has_booking_url,total)<10} />
            <FieldBar label="Specialties URL" fill={s.has_specialties_url||0}total={total} color="#d97706" warn={pctN(s.has_specialties_url,total)<10} />
            <FieldBar label="Place ID"        fill={s.has_place_id||0}       total={total} color="#e05c5c" critical />
            <div style={{ marginTop:14, paddingTop:12, borderTop:`1px solid ${t.bd}` }}>
              <div style={{ fontSize:'.62rem', color:t.mut, textTransform:'uppercase', letterSpacing:'.06em', marginBottom:6 }}>Premium threshold</div>
              <div style={{ fontSize:'.71rem', color:t.mut, lineHeight:1.5 }}>
                <strong style={{ color:'#9F7E4F' }}>Premium</strong> = phone + website + booking URL + specialties URL.{' '}
                <strong style={{ color:'#9F7E4F' }}>{fmt(s.premium||0)}</strong> ({pct(s.premium,total)}) currently qualify.{' '}
                Biggest lever: enriching <strong>booking_url</strong> ({fmt(s.missing_booking||0)} missing) and <strong>specialties_url</strong> ({fmt(s.missing_specialties||0)} missing).
              </div>
            </div>
          </>}
        </Card>
      </div>

      {/* ── Pipeline status ── */}
      <Card>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
          <CardTitle>Pipeline status</CardTitle>
          <span style={{ fontSize:'.62rem', color:t.mut }}>geocoding · enrichment · verification</span>
        </div>
        {loading ? <div style={{ color:t.mut, fontSize:'.75rem' }}>Loading…</div> : <>
          <PipelineBar label="Geocoding (GPS coordinates + Place ID)" done={s.geocode_done||0} partial={0} failed={s.geocode_failed||0} nullCount={s.geocode_null||0} total={total} />
          <PipelineBar label="Enrichment (booking URL, specialties, details)" done={s.enrich_done||0} partial={s.enrich_partial||0} failed={s.enrich_failed||0} nullCount={s.enrich_null||0} total={total} />
          <PipelineBar label="Verification (confirm names, addresses, contacts)" done={s.verify_done||0} partial={0} failed={s.verify_failed||0} nullCount={s.verify_null||0} total={total} />
          <div style={{ display:'flex', gap:16, marginTop:8, flexWrap:'wrap' }}>
            {[['#2da06a','Done'],['#6F8FA7','Partial'],['#e05c5c','Failed'],['#dde8ef','Not started']].map(([c,l]) => (
              <span key={l} style={{ display:'flex', alignItems:'center', gap:5, fontSize:'.62rem', color:t.mut }}>
                <span style={{ width:8, height:8, borderRadius:2, background:c, display:'inline-block' }} />{l}
              </span>
            ))}
          </div>
        </>}
      </Card>

      {/* ── Country / Continent table ── */}
      <Card>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
          <CardTitle>Quality breakdown</CardTitle>
          <div style={{ display:'flex', gap:6 }}>
            <TabBtn id="country" label="By country" />
            <TabBtn id="continent" label="By continent" />
          </div>
        </div>

        {activeTab === 'country' && (
          loading ? <div style={{ color:t.mut, fontSize:'.75rem' }}>Loading…</div> : (
            <div style={{ overflowX:'auto' }}>
              <div style={{ minWidth:580 }}>
                <div style={{ display:'grid', gridTemplateColumns:'1.4fr .6fr .6fr .6fr .6fr .6fr .7fr .7fr', gap:6, paddingBottom:7, borderBottom:`1px solid ${t.bd}`, marginBottom:4 }}>
                  {[['country','Country'],['total','Total'],['premium','Premium'],['has_coords','GPS'],['has_phone','Phone'],['has_booking','Booking'],['enrich_done','Enriched'],['enrich_failed','Failed']].map(([col,lbl]) => (
                    <SortHd key={col} col={col} label={lbl} sortCol={sortCol} sortAsc={sortAsc} onSort={toggleSort} />
                  ))}
                </div>
                {sortedCountries.map(c => {
                  const premPct  = pctN(c.premium,   c.total);
                  const coordPct = pctN(c.has_coords, c.total);
                  const bookPct  = pctN(c.has_booking,c.total);
                  const enrichPct= pctN(c.enrich_done,c.total);
                  const failPct  = pctN(c.enrich_failed,c.total);
                  return (
                    <div key={c.country} style={{ ...shRow, gridTemplateColumns:'1.4fr .6fr .6fr .6fr .6fr .6fr .7fr .7fr' }}>
                      <span style={{ fontSize:'.76rem', color:t.tx, fontWeight:500 }}>{c.country}</span>
                      <span style={{ fontSize:'.72rem', color:t.mut, fontVariantNumeric:'tabular-nums' }}>{fmt(c.total)}</span>
                      <span style={{ fontSize:'.7rem',  color: premPct>5 ? '#9F7E4F':'#9eb8c8', fontWeight:premPct>5?600:400 }}>{premPct}%</span>
                      <span style={{ fontSize:'.7rem',  color: coordPct>50?'#2da06a':coordPct>10?'#d97706':'#e05c5c', fontWeight:500 }}>{coordPct}%</span>
                      <span style={{ fontSize:'.7rem',  color:t.mut }}>{pctN(c.has_phone,c.total)}%</span>
                      <span style={{ fontSize:'.7rem',  color: bookPct>20?'#2da06a':'#d97706' }}>{bookPct}%</span>
                      <span style={{ fontSize:'.7rem',  color: enrichPct>50?'#2da06a':'#6F8FA7' }}>{enrichPct}%</span>
                      <span style={{ fontSize:'.7rem',  color: failPct>20?'#e05c5c':'#9eb8c8' }}>{failPct}%</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )
        )}

        {activeTab === 'continent' && (
          loading ? <div style={{ color:t.mut, fontSize:'.75rem' }}>Loading…</div> : (
            <div style={{ overflowX:'auto' }}>
              <div style={{ minWidth:520 }}>
                <div style={{ display:'grid', gridTemplateColumns:'1.2fr .6fr .6fr .6fr .6fr .7fr .7fr', gap:6, paddingBottom:7, borderBottom:`1px solid ${t.bd}`, marginBottom:4 }}>
                  {[['continent','Continent'],['total','Total'],['premium','Premium'],['has_coords','GPS'],['has_booking','Booking'],['enrich_done','Enriched'],['enrich_failed','Failed']].map(([col,lbl]) => (
                    <SortHd key={col} col={col} label={lbl} sortCol={cSortCol} sortAsc={cSortAsc} onSort={toggleCSort} />
                  ))}
                </div>
                {sortedContinents.map(c => {
                  const premPct  = pctN(c.premium,    c.total);
                  const coordPct = pctN(c.has_coords,  c.total);
                  const bookPct  = pctN(c.has_booking, c.total);
                  const enrichPct= pctN(c.enrich_done, c.total);
                  const failPct  = pctN(c.enrich_failed,c.total);
                  return (
                    <div key={c.continent} style={{ ...shRow, gridTemplateColumns:'1.2fr .6fr .6fr .6fr .6fr .7fr .7fr' }}>
                      <span style={{ fontSize:'.78rem', color:t.tx, fontWeight:600 }}>{c.continent}</span>
                      <span style={{ fontSize:'.74rem', color:t.mut, fontVariantNumeric:'tabular-nums' }}>{fmt(c.total)}</span>
                      <span style={{ fontSize:'.72rem', color: premPct>5?'#9F7E4F':'#9eb8c8', fontWeight:premPct>5?600:400 }}>{premPct}%</span>
                      <span style={{ fontSize:'.72rem', color: coordPct>50?'#2da06a':coordPct>10?'#d97706':'#e05c5c', fontWeight:500 }}>{coordPct}%</span>
                      <span style={{ fontSize:'.72rem', color: bookPct>20?'#2da06a':'#d97706' }}>{bookPct}%</span>
                      <span style={{ fontSize:'.72rem', color: enrichPct>50?'#2da06a':'#6F8FA7' }}>{enrichPct}%</span>
                      <span style={{ fontSize:'.72rem', color: failPct>20?'#e05c5c':'#9eb8c8' }}>{failPct}%</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )
        )}
      </Card>

      {/* ── Flagged records ── */}
      <Card>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10 }}>
          <CardTitle>Flagged records — needs review ({flagged.length})</CardTitle>
          <div style={{ display:'flex', gap:5, flexWrap:'wrap' }}>
            {Object.entries(flagGroups).map(([reason, n]) => (
              <span key={reason} style={{ fontSize:'.59rem', padding:'2px 7px', borderRadius:8, background:(FLAG_COLORS[reason]||'#6F8FA7')+'18', color:FLAG_COLORS[reason]||'#6F8FA7', fontWeight:600 }}>
                {FLAG_LABELS[reason]||reason}: {n}
              </span>
            ))}
          </div>
        </div>

        {loading ? <div style={{ color:t.mut, fontSize:'.75rem' }}>Loading…</div>
        : flagged.length === 0 ? <Insight type="ok">No flagged records — names and addresses all look clean.</Insight>
        : <>
            <div style={{ overflowX:'auto' }}>
              <div style={{ minWidth:580 }}>
                <div style={{ display:'grid', gridTemplateColumns:'.4fr 1.5fr 1.5fr .7fr .8fr .8fr', gap:6, paddingBottom:7, borderBottom:`1px solid ${t.bd}`, marginBottom:4 }}>
                  {['ID','Name','Address','Country','Issue','Action'].map(h => (
                    <span key={h} style={{ fontSize:'.57rem', color:t.mut, textTransform:'uppercase', letterSpacing:'.06em' }}>{h}</span>
                  ))}
                </div>
                {flaggedPage.map(r => (
                  <div key={r.id} style={{ ...shRow, gridTemplateColumns:'.4fr 1.5fr 1.5fr .7fr .8fr .8fr' }}>
                    <span style={{ fontSize:'.59rem', fontFamily:'monospace', color:t.mut, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{r.id}</span>
                    <span style={{ fontSize:'.7rem', color:t.tx, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }} title={r.name}>
                      {r.name || <em style={{ color:'#e05c5c' }}>missing</em>}
                    </span>
                    <span style={{ fontSize:'.67rem', color:getDisplayAddress(r)?'#6F8FA7':'#e05c5c', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }} title={getDisplayAddress(r)}>
                      {getDisplayAddress(r) || <em>missing</em>}
                    </span>
                    <span style={{ fontSize:'.67rem', color:t.mut }}>{r.country||'—'}</span>
                    <span style={{ fontSize:'.61rem', padding:'2px 6px', borderRadius:7, background:(FLAG_COLORS[r.flag_reason]||'#6F8FA7')+'18', color:FLAG_COLORS[r.flag_reason]||'#6F8FA7', fontWeight:600, whiteSpace:'nowrap' }}>
                      {FLAG_LABELS[r.flag_reason]||r.flag_reason}
                    </span>
                    <div style={{ display:'flex', gap:4 }}>
                      <button onClick={() => setEditing(r)} style={{ padding:'3px 9px', background:t.accent, color:'#fff', border:'none', borderRadius:5, fontSize:'.65rem', fontWeight:600, cursor:'pointer', fontFamily:F }}>Edit</button>
                      <button onClick={() => navigator.clipboard.writeText(r.id).catch(()=>{})} style={{ padding:'3px 6px', background:t.rowbg, color:t.accent, border:'none', borderRadius:5, fontSize:'.62rem', cursor:'pointer', fontFamily:F }}>ID</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            {flagged.length > FLAG_PAGE_SIZE && (
              <div style={{ display:'flex', gap:8, alignItems:'center', marginTop:12, justifyContent:'flex-end' }}>
                <span style={{ fontSize:'.64rem', color:t.mut }}>{flagPage*FLAG_PAGE_SIZE+1}–{Math.min((flagPage+1)*FLAG_PAGE_SIZE,flagged.length)} of {flagged.length}</span>
                <Btn small variant="outline" onClick={() => setFlagPage(p=>Math.max(0,p-1))} disabled={flagPage===0}>← Prev</Btn>
                <Btn small variant="outline" onClick={() => setFlagPage(p=>p+1)} disabled={(flagPage+1)*FLAG_PAGE_SIZE>=flagged.length}>Next →</Btn>
              </div>
            )}
          </>
        }
      </Card>
    </div>
  );
}

export default function DataQuality(props) {
  if (props && props.embed) return <DataQualityBody {...props} />;
  return <ConsoleShell title="Data Quality"><DataQualityBody {...props} /></ConsoleShell>;
}
